/**
 * BookOnBehalfModal — launched from /me to let an advisor (or admin)
 * book a meeting directly onto a specific advisor's calendar without
 * going through the public qualifying form.
 *
 * Used for:
 *   - 2nd-5th meetings, application meetings, anniversary reviews
 *   - Reschedules captured by phone the advisor wants to log
 *   - Admin-triggered bookings (Zach booking on behalf of an advisor)
 *
 * Flow:
 *   1. Advisor selection — non-admin advisors see only themselves
 *      pre-filled and locked. Admins get a dropdown of all active
 *      advisors. (Admin-only feature; PR #30 ships read-only for
 *      non-admins.)
 *   2. Prospect lookup — autocomplete via /api/prospect-suggest with
 *      a 200ms debounce. Picking a suggestion populates first/last/
 *      email/phone. Manual override always wins.
 *   3. Meeting type — dropdown of active meeting_types.
 *   4. Slot picker — re-uses <SlotPicker /> in advisor-first mode.
 *   5. Confirm → POST /api/book with ruleHit='BOOK_ON_BEHALF' and
 *      the chosen meetingTypeSlug. Server runs the same provisioning
 *      as the public flow (Zoom + GCal + email + SMS).
 *   6. Success → show "Booking created" toast and close.
 *
 * NOT a private surface — bookings made here trigger the same emails
 * and Zoom invites as a public booking, so the prospect always knows.
 */

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { authedFetch } from '../lib/authed-fetch';
import { SlotPicker } from './SlotPicker';
import { postBook } from '../lib/api/client';
import type {
  AvailabilitySlot,
  BookRequest,
  BookResponse,
  QualifyAdvisorFirst,
} from '../lib/api/types';
// SlotPicker styles live in App.css. The /me page doesn't otherwise
// import that file, so the slot picker would render unstyled inside the
// modal without this. Importing here means anyone who renders the
// modal — /me today, future surfaces tomorrow — gets the right styles.
import '../App.css';
import './BookOnBehalfModal.css';

interface AdvisorOption {
  id: string;
  fullName: string;
  firstName: string;
  email: string;
  isActive: boolean;
  slug: string | null;
}

interface MeetingTypeOption {
  id: string;
  slug: string;
  displayName: string;
  durationMinutes: number;
  isActive: boolean;
}

interface ProspectSuggestion {
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  lastSeenUtc: string;
  meetingCount: number;
}

interface Props {
  /** The current user's advisor record, or null if they're admin-only. */
  selfAdvisor: AdvisorOption | null;
  /** True when the current user is an admin (can book for any advisor). */
  isAdmin: boolean;
  onClose: () => void;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9\-().\s]{7,}$/;

type Phase =
  | { state: 'editing' }
  | { state: 'picking'; selectedAdvisorId: string }
  | { state: 'submitting' }
  | { state: 'success'; meetingStartUtc: string; advisorFirstName: string };

export function BookOnBehalfModal({ selfAdvisor, isAdmin, onClose }: Props) {
  // ---------- form state ----------
  const [advisors, setAdvisors] = useState<AdvisorOption[]>(
    selfAdvisor ? [selfAdvisor] : []
  );
  const [meetingTypes, setMeetingTypes] = useState<MeetingTypeOption[]>([]);
  const [advisorId, setAdvisorId] = useState<string>(selfAdvisor?.id ?? '');
  const [meetingTypeSlug, setMeetingTypeSlug] = useState<string>('2nd-meeting');
  // Meeting length (minutes). Only Application Meetings and Annuity
  // Policy Delivery Reviews may deviate from 60 — the picker below only
  // renders for those two types and resets to 60 on type change.
  const [durationMinutes, setDurationMinutes] = useState<number>(60);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  // Prospect autocomplete
  const [suggestions, setSuggestions] = useState<ProspectSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef<number | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<Phase>({ state: 'editing' });
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---------- bootstrap: load meeting types + (admin-only) advisors ----------
  useEffect(() => {
    let cancelled = false;

    // Meeting types — uses the existing /api/templates/meeting-types
    // endpoint (built for the portal /templates editor).
    type MTRow = { id: string; slug: string; displayName: string; displayOrder: number };
    authedFetch('/api/templates/meeting-types')
      .then(async (r) =>
        r.ok ? ((await r.json()) as { ok: true; meetingTypes: MTRow[] }) : null
      )
      .then((res) => {
        if (cancelled || !res || !res.ok) return;
        setMeetingTypes(
          res.meetingTypes.map((r) => ({
            id: r.id,
            slug: r.slug,
            displayName: r.displayName,
            durationMinutes: 60,
            isActive: true,
          }))
        );
      })
      .catch(() => {
        // Fallback list — at least let the form work even if the
        // meeting-types endpoint is unavailable. Slugs match seed data.
        if (cancelled) return;
        setMeetingTypes([
          { id: '', slug: '2nd-meeting', displayName: '2nd Meeting', durationMinutes: 60, isActive: true },
          { id: '', slug: '3rd-meeting', displayName: '3rd Meeting', durationMinutes: 60, isActive: true },
          { id: '', slug: 'application-meeting', displayName: 'Application Meeting', durationMinutes: 60, isActive: true },
          { id: '', slug: 'annuity-anniversary', displayName: 'Annuity Anniversary', durationMinutes: 60, isActive: true },
        ]);
      });

    // Admin-only: load all active advisors so admins can book for anyone.
    if (isAdmin) {
      type AdminAdvisorRow = {
        id: string;
        fullName: string;
        email: string;
        isActive: boolean;
      };
      authedFetch('/api/admin/advisors')
        .then(async (r) =>
          r.ok ? ((await r.json()) as { ok: true; advisors: AdminAdvisorRow[] }) : null
        )
        .then((res) => {
          if (cancelled || !res || !res.ok) return;
          const mapped: AdvisorOption[] = res.advisors
            .filter((a) => a.isActive)
            .map((a) => ({
              id: a.id,
              fullName: a.fullName,
              firstName: a.fullName.split(' ')[0] ?? a.fullName,
              email: a.email,
              isActive: a.isActive,
              slug: null,
            }));
          setAdvisors(mapped);
          // Default the dropdown to selfAdvisor when present, otherwise
          // the first listed advisor.
          if (!selfAdvisor && mapped.length > 0) {
            setAdvisorId(mapped[0].id);
          }
        })
        .catch(() => {
          /* non-fatal — admins can still pick selfAdvisor if any */
        });
    }

    return () => {
      cancelled = true;
    };
  }, [isAdmin, selfAdvisor]);

  // ---------- prospect autocomplete ----------
  useEffect(() => {
    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
    }
    const q = (email || firstName || lastName).trim();
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = window.setTimeout(() => {
      const params = new URLSearchParams({ q });
      // Non-admins are server-scoped to their own bookings; passing the
      // advisorId param is informational (and admins can use it to scope
      // suggestions to the advisor they're booking for).
      if (advisorId) params.set('advisorId', advisorId);
      authedFetch(`/api/prospect-suggest?${params.toString()}`)
        .then(async (r) =>
          r.ok ? ((await r.json()) as { ok: true; suggestions: ProspectSuggestion[] }) : null
        )
        .then((res) => {
          if (!res || !res.ok) return;
          setSuggestions(res.suggestions);
        })
        .catch(() => {
          /* ignore suggest failures */
        });
    }, 200);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [email, firstName, lastName, advisorId]);

  function applySuggestion(s: ProspectSuggestion) {
    setFirstName(s.firstName);
    setLastName(s.lastName);
    setEmail(s.email);
    if (s.phone) setPhone(s.phone);
    setShowSuggestions(false);
  }

  // ---------- validation + submit ----------
  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!advisorId) e.advisorId = 'Pick an advisor';
    if (!meetingTypeSlug) e.meetingType = 'Pick a meeting type';
    if (!firstName.trim()) e.firstName = 'Required';
    if (!lastName.trim()) e.lastName = 'Required';
    if (!email.trim()) e.email = 'Required';
    else if (!EMAIL_RE.test(email)) e.email = 'Enter a valid email';
    if (!phone.trim()) e.phone = 'Required';
    else if (!PHONE_RE.test(phone)) e.phone = 'Enter a valid phone';
    return e;
  }

  const selectedAdvisor = useMemo(
    () => advisors.find((a) => a.id === advisorId) ?? null,
    [advisors, advisorId]
  );

  const canPickDuration =
    meetingTypeSlug === 'application-meeting' ||
    meetingTypeSlug === 'annuity-policy-delivery-review';

  function handleNext(ev: FormEvent<HTMLFormElement>) {
    ev.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;
    setSubmitError(null);
    setPhase({ state: 'picking', selectedAdvisorId: advisorId });
  }

  async function confirmSlot(slot: AvailabilitySlot, tz: string) {
    if (phase.state !== 'picking' || !selectedAdvisor) return;
    setPhase({ state: 'submitting' });
    setSubmitError(null);

    const reqBody: BookRequest = {
      advisorId: selectedAdvisor.id,
      poolSlug: null,
      slotStartUtc: slot.startUtc,
      email: email.trim(),
      phone: phone.trim(),
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      ruleHit: 'BOOK_ON_BEHALF',
      meetingTypeSlug,
      ...(canPickDuration ? { durationMinutes } : {}),
      prospectTimezone: tz,
      utm_source: 'book-on-behalf',
      utm_medium: 'internal',
      utm_campaign: meetingTypeSlug,
    };

    try {
      const res: BookResponse = await postBook(reqBody);
      if (res.ok) {
        setPhase({
          state: 'success',
          meetingStartUtc: res.meetingStartUtc,
          advisorFirstName: selectedAdvisor.firstName,
        });
        return;
      }
      if (res.error === 'slot_unavailable') {
        setPhase({ state: 'picking', selectedAdvisorId: selectedAdvisor.id });
        setSubmitError('That time was just taken. Please pick another.');
        return;
      }
      setPhase({ state: 'picking', selectedAdvisorId: selectedAdvisor.id });
      setSubmitError(res.message);
    } catch {
      setPhase({ state: 'picking', selectedAdvisorId: selectedAdvisor.id });
      setSubmitError('Network error during booking. Please try again.');
    }
  }

  // ---------- escape key + click-outside ----------
  useEffect(() => {
    function onKey(ev: KeyboardEvent) {
      if (ev.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ---------- render ----------
  // Build the fake QualifySuccess for SlotPicker reuse in the picker phase.
  const fakeQualify: QualifyAdvisorFirst | null =
    phase.state === 'picking' && selectedAdvisor
      ? {
          ok: true,
          kind: 'advisor_first',
          sessionId: 'book-on-behalf',
          advisor: {
            id: selectedAdvisor.id,
            firstName: selectedAdvisor.firstName,
            lastName: '',
          },
          ruleHit: 'OWNERSHIP_LEAD',
          poolSlug: null,
          minLeadTimeMinutes: 60, // shorter floor for internal bookings
          effectiveLeadTimeMinutes: 60,
          meetingDurationMinutes: canPickDuration ? durationMinutes : 60,
        }
      : null;

  return (
    <div className="bob-overlay" role="dialog" aria-modal="true" aria-labelledby="bob-title">
      <div className="bob-modal">
        <header className="bob-header">
          <h2 id="bob-title">
            {phase.state === 'success'
              ? 'Booking created'
              : phase.state === 'picking'
                ? 'Pick a time'
                : 'Book on behalf'}
          </h2>
          <button
            type="button"
            className="bob-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {phase.state === 'editing' && (
          <form onSubmit={handleNext} className="bob-body">
            {/* Advisor picker — locked for non-admins, dropdown for admins */}
            <div className="bob-row">
              <label htmlFor="bob-advisor">Advisor</label>
              {isAdmin && advisors.length > 1 ? (
                <select
                  id="bob-advisor"
                  value={advisorId}
                  onChange={(e) => setAdvisorId(e.target.value)}
                  aria-invalid={!!errors.advisorId}
                >
                  {advisors.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.fullName}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  id="bob-advisor"
                  type="text"
                  value={selectedAdvisor?.fullName ?? ''}
                  disabled
                  readOnly
                />
              )}
              {errors.advisorId && <span className="field-error">{errors.advisorId}</span>}
            </div>

            <div className="bob-row">
              <label htmlFor="bob-mt">Meeting type</label>
              <select
                id="bob-mt"
                value={meetingTypeSlug}
                onChange={(e) => {
                  const slug = e.target.value;
                  setMeetingTypeSlug(slug);
                  if (
                    slug !== 'application-meeting' &&
                    slug !== 'annuity-policy-delivery-review'
                  ) {
                    setDurationMinutes(60);
                  }
                }}
                aria-invalid={!!errors.meetingType}
              >
                {meetingTypes.map((m) => (
                  <option key={m.slug} value={m.slug}>
                    {m.displayName} ({m.durationMinutes}m)
                  </option>
                ))}
              </select>
              {errors.meetingType && <span className="field-error">{errors.meetingType}</span>}
            </div>

            {/* Meeting length — advisors/admins only, and only for the
                two variable-length types (Zach, 7/9/26). */}
            {canPickDuration && (
              <div className="bob-row">
                <label htmlFor="bob-duration">Meeting length</label>
                <select
                  id="bob-duration"
                  value={durationMinutes}
                  onChange={(e) => setDurationMinutes(Number(e.target.value))}
                >
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                  <option value={180}>3 hours</option>
                </select>
              </div>
            )}

            {/* Prospect — name, email, phone, with autocomplete on email */}
            <div className="bob-grid-2">
              <div className="bob-row">
                <label htmlFor="bob-first">Prospect first name</label>
                <input
                  id="bob-first"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  onFocus={() => setShowSuggestions(true)}
                  maxLength={50}
                  aria-invalid={!!errors.firstName}
                  autoComplete="off"
                />
                {errors.firstName && <span className="field-error">{errors.firstName}</span>}
              </div>
              <div className="bob-row">
                <label htmlFor="bob-last">Prospect last name</label>
                <input
                  id="bob-last"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={50}
                  aria-invalid={!!errors.lastName}
                  autoComplete="off"
                />
                {errors.lastName && <span className="field-error">{errors.lastName}</span>}
              </div>
            </div>

            <div className="bob-row bob-suggest-anchor">
              <label htmlFor="bob-email">Prospect email</label>
              <input
                id="bob-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                maxLength={120}
                aria-invalid={!!errors.email}
                autoComplete="off"
              />
              {errors.email && <span className="field-error">{errors.email}</span>}
              {showSuggestions && suggestions.length > 0 && (
                <ul className="bob-suggestions" role="listbox">
                  {suggestions.map((s) => (
                    <li key={s.email}>
                      <button
                        type="button"
                        className="bob-suggest-item"
                        onMouseDown={(ev) => {
                          ev.preventDefault();
                          applySuggestion(s);
                        }}
                      >
                        <span className="bob-suggest-name">
                          {s.firstName} {s.lastName}
                        </span>
                        <span className="bob-suggest-email">{s.email}</span>
                        {s.meetingCount > 1 && (
                          <span className="bob-suggest-count">
                            {s.meetingCount} prior meetings
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="bob-row">
              <label htmlFor="bob-phone">Prospect phone</label>
              <input
                id="bob-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={32}
                aria-invalid={!!errors.phone}
              />
              {errors.phone && <span className="field-error">{errors.phone}</span>}
            </div>

            <footer className="bob-footer">
              <button type="button" className="bob-secondary-btn" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="bob-primary-btn">
                Pick a time →
              </button>
            </footer>
          </form>
        )}

        {phase.state === 'picking' && fakeQualify && selectedAdvisor && (
          <div className="bob-body">
            <p className="bob-context">
              Booking a <strong>{meetingTypes.find((m) => m.slug === meetingTypeSlug)?.displayName ?? meetingTypeSlug}</strong> with{' '}
              <strong>{selectedAdvisor.fullName}</strong> for <strong>{email}</strong>.
            </p>
            {submitError && <p className="bob-err">{submitError}</p>}
            <SlotPicker
              match={fakeQualify}
              windowDays={meetingTypeSlug === 'annuity-anniversary' ? 395 : meetingTypeSlug && meetingTypeSlug !== '1st-meeting' ? 90 : 30}
              meetingTypeSlug={meetingTypeSlug}
              durationMinutes={canPickDuration ? durationMinutes : undefined}
              onSelectSlot={confirmSlot}
              onBack={() => setPhase({ state: 'editing' })}
            />
          </div>
        )}

        {phase.state === 'submitting' && (
          <div className="bob-body bob-loading">
            <p>Confirming booking…</p>
          </div>
        )}

        {phase.state === 'success' && (
          <div className="bob-body bob-success">
            <p>
              Booked with <strong>{phase.advisorFirstName}</strong> for{' '}
              <strong>{formatLocal(phase.meetingStartUtc)}</strong>. Confirmation
              email + Zoom invite have been sent to the prospect.
            </p>
            <footer className="bob-footer">
              <button type="button" className="bob-primary-btn" onClick={onClose}>
                Done
              </button>
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}

function formatLocal(iso: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }).format(new Date(iso));
}
